import { ROUTES } from '@/constants/routes';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { getConnections } from '@/store/reducers/plaid/actions';
import { setIsPlaidLoginRequired } from '@/store/reducers/plaid/plaidSlice';
import { getUser } from '@/store/reducers/user/actions';
import { setUserFromMobile } from '@/store/reducers/user/userSlice';
import { Connection } from '@/types/plaid';
import { localStorage } from '@/utils/storage';
import { useEffect, useState } from 'react';
import { selectSurveyIsCompleted, setCurrentStep } from '@/store/reducers/survey/surveySlice';
import { useCollectClickId } from './useCollectClickId';
import { useNavigateWithParams } from './useNavigateWithParams';
import useSurveyNavigate from '@/hooks/useSurveyNavigate';

export function useBootstrap() {
  const navigate = useNavigate();
  const navigateWithParams = useNavigateWithParams();
  const dispatch = useAppDispatch();
  const [params] = useSearchParams();

  const [isLoading, setIsLoading] = useState(true);
  const surveyIsCompleted = useAppSelector(selectSurveyIsCompleted);
  const { currentUrl: currentSurveyUrl } = useSurveyNavigate();

  useCollectClickId();

  const loadBankConnections = async () => {
    const connections = await dispatch(getConnections()).unwrap();

    return connections;
  };

  const getUserState = async () => {
    const accessToken = await localStorage.get('accessToken');
    const isUserFromMobileStorage = await localStorage.get('isUserFromMobile');
    const isUserFromMobile = Boolean(params.get('isMobile')) || isUserFromMobileStorage;

    return {
      accessToken,
      isUserFromMobile,
    };
  };

  const handleAuthenticatedUser = async () => {
    await dispatch(getUser());

    if (window.location.href.includes('oauth_state_id')) {
      return;
    }

    const connections = await loadBankConnections();

    if (connections.length) {
      const isLoginRequired = connections.some(
        (connection: Connection) => connection.isLoginRequired,
      );

      if (isLoginRequired) {
        dispatch(setIsPlaidLoginRequired(isLoginRequired));
      }
    }

    return navigateWithParams(surveyIsCompleted ? ROUTES.DOWNLOAD_APP : currentSurveyUrl);
  };

  const setupUserFromMobile = (isUserFromMobile: boolean) => {
    localStorage.save('isUserFromMobile', isUserFromMobile);

    dispatch(setUserFromMobile(isUserFromMobile));
  };

  useEffect(() => {
    const start = async () => {
      const { accessToken, isUserFromMobile } = await getUserState();

      try {
        setupUserFromMobile(isUserFromMobile);

        if (accessToken) {
          return await handleAuthenticatedUser();
        }

        if (isUserFromMobile) {
          dispatch(setCurrentStep(15));

          return navigate(`${ROUTES.SURVEY}?step=${15}`);
        } else {
          return navigateWithParams(currentSurveyUrl);
        }
      } catch (error) {
        if (isUserFromMobile) {
          dispatch(setCurrentStep(15));

          return navigate(`${ROUTES.SURVEY}?step=${15}`);
        } else {
          return navigateWithParams(ROUTES.SURVEY);
        }
      } finally {
        setIsLoading(false);
      }
    };

    start();
  }, []);

  return { isLoading };
}
